import { Inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { debounceTime, filter, map, switchMap, take, tap } from 'rxjs/operators'
import { PerfectScrollbarActions } from './actions-type'
import { Store } from '@ngrx/store'
import {
    selectOriginFeatureByKey,
    selectPerfectScrollbar, selectPerfectScrollbarByKey,
    selectPerfectScrollbarFeature,
} from './perfect-scrollbar.selectors'
import { Observable, of } from 'rxjs'
import * as Ps from 'perfect-scrollbar'

export interface PreFetchResul {
    fetch: boolean;
}

@Injectable()
export class PerfectScrollbarEffects {

    setOrifinFilterCriteria$: Observable<any>
    setSearchText$: Observable<any>
    clearSearchTextToReloadFilter$: Observable<any>
    initPerfectScrollBar$: Observable<any>
    filterChanged$: Observable<any>
    fetchResult$: Observable<any>
    refetchAfterClearSearch$: Observable<any>
    postLoadAction$: Observable<any>
    updatePerfetScrollbar$: Observable<any>
    visibleChange$: Observable<any>

    private itemsPerPage = 20
    private noFetch = { fetch: false }
    private fetch = { fetch: true }

    constructor(
        @Inject(Actions) private actions$: Actions,
        @Inject(Store) private store: Store
    ) {
        this.setOrifinFilterCriteria$ = createEffect(() => this.actions$.pipe(
            ofType(PerfectScrollbarActions.init),
            switchMap(action =>
                this.store.select(selectOriginFeatureByKey(action.globalStatePath ?? action.key, 'filterCriteria', action.targetKey)).pipe(
                    take(1),
                    map(originFilterCriteria => {
                        // @ts-ignore
                        if (!originFilterCriteria?.limit) { // default items per page for origin component if not defined
                            return PerfectScrollbarActions.updateOriginFilterCriteria({key: action.globalStatePath ?? action.key, fragment: { limit: this.itemsPerPage }})
                        }
                        return { type: '[Perfect Scrollbar] No Action' }
                    })
                )
            )
        ))

        // init PerfectScrollbar plugin in DOM
        this.initPerfectScrollBar$ = createEffect(() => this.actions$.pipe(
            ofType(PerfectScrollbarActions.init),
            tap(action => this.initPerfectScrollbar(action.key, action.psSelector).then(()=> {
                // if searchText property in filterCriteria is not empty => start loading data
                this.checkInitialLoad(action.key, action.targetKey, action.initialLoad)
            }))
        ), { dispatch: false })

        // When Origin component dispatch setSearchText, asynchronous update origin searchText Also
        this.setSearchText$ = createEffect(() => this.actions$.pipe(
            ofType(PerfectScrollbarActions.setSearchText),
            map(({ key, searchText }) => PerfectScrollbarActions.updateOriginSearchText({ key, fragment: { offset: 0, searchText } }),
            ))
        )
        this.clearSearchTextToReloadFilter$ = createEffect(() => this.actions$.pipe(
            ofType(PerfectScrollbarActions.clearSearchTextToReloadFilter),
            map(({ key }) => PerfectScrollbarActions.updateOriginSearchText({ key, fragment: { offset: 0, searchText: undefined } }),
            ))
        )

        this.filterChanged$ = createEffect(() => this.actions$.pipe(
            ofType(PerfectScrollbarActions.filterChanged),
            switchMap(({ key, initialLoad, abortOriginFetch }) => this.prepareFetch(key, initialLoad, abortOriginFetch === true).pipe(
                map(result => {
                    if (result.fetch) {
                        this.store.dispatch(PerfectScrollbarActions.setLoadState({ key: key, loadState: true }))
                        return PerfectScrollbarActions.fetchOriginData({key})
                    } else {
                        return { type: `[Perfect Scrollbar] No Action for ${key}` }
                    }
                })
            )))
        )

        // When SearchText setup done, check PS settings before fetch from origin component
        this.fetchResult$ = createEffect(() => this.actions$.pipe(
            ofType(PerfectScrollbarActions.updateOriginSearchText),
                // SwitchMap on actions$ stream - if setSearchText changed, cancel all previous fetchResult and run new
                switchMap(({ key }) => this.prepareFetch(key, false, true).pipe(
                    map(result => {
                        if (result.fetch) {
                            return PerfectScrollbarActions.fetchOriginData({key})
                        } else {
                            return { type: '[Default] No Action' }
                        }
                    })
                )
            ))
        )

        // DEV-74: After clearing search text in list components (initialLoad=true),
        // wait for input to settle (debounce handles paste = clear+set),
        // then refetch if search text is still empty.
        this.refetchAfterClearSearch$ = createEffect(() => this.actions$.pipe(
            ofType(PerfectScrollbarActions.updateOriginSearchText),
            filter(({ fragment }) => fragment?.searchText !== undefined && !fragment?.searchText),
            debounceTime(150),
            switchMap(({ key }) => this.store.select(selectPerfectScrollbar).pipe(
                take(1),
                switchMap((state: any) => {
                    if (state[key]?.refetchOnClearSearch && !state[key]?.searchText) {
                        return of(PerfectScrollbarActions.filterChanged({ key, initialLoad: true, abortOriginFetch: true }))
                    }
                    return of({ type: '[Perfect Scrollbar] No refetch needed' })
                })
            ))
        ))

        this.postLoadAction$ = createEffect(() => this.actions$.pipe(
            ofType(PerfectScrollbarActions.postLoadAction),
            tap(action => this.postLoadAction(action.key, action.data))),
            { dispatch: false }  // Add this if postLoadAction does not dispatch an action
        )

        this.visibleChange$ = createEffect(() => this.actions$.pipe(
            ofType(PerfectScrollbarActions.visibleChange),
            tap(action => {
                if (action.isVisible) {
                    const container: HTMLElement = document.getElementById(action.psSelector)
                    if (container) {
                        this.handleLazyLoad(action.key, container.scrollTop,
                            container.scrollHeight - container.offsetHeight)

                        // PS scrollbar was missing without setTimeout
                        setTimeout(() => {
                            Ps.update(container)
                            Ps.update(container) // solves problem when height of container decreases
                        })
                    }
                }
            }),
        ), { dispatch: false})

        this.updatePerfetScrollbar$ = createEffect(() => this.actions$.pipe(
            ofType(PerfectScrollbarActions.setOriginItemCollection),
            tap(action => {
                this.store.select(selectPerfectScrollbarByKey(action.key)).pipe(take(1)).subscribe(ps => {
                    const container: HTMLElement = document.getElementById(ps.psSelector)
                    // PS scrollbar was missing without setTimeout
                    setTimeout(() => {
                        Ps.update(container)
                        Ps.update(container) // solves problem when height of container decreases
                    })
                })
            })),
            { dispatch: false }
        )
    }

    private prepareFetch(key: string, initialLoad = false, abortOriginFetch = false): Observable<PreFetchResul> {
        return this.store.select(selectPerfectScrollbarFeature).pipe(take(1),(
            switchMap((state) => {
                if (!state[key]?.globalStatePath) {
                    return of(this.noFetch)  // global state path is not set anymore
                }
                if (state[key].isVisible !== undefined && state[key].isVisible === false) {
                    this.store.dispatch(PerfectScrollbarActions.setLoadState({ key: key, loadState: false }))
                    if (abortOriginFetch) {
                        this.store.dispatch(PerfectScrollbarActions.setOriginItemCollection({ key: key, collection: null }))
                    }
                    return of(this.noFetch)
                }

                // DEV-1942: Když už fetch běží, přefetchne se vždy — starý request tím odpadne.
                //
                // Dřív se změna filtru v tomhle případě zahodila (setFlushCache + noFetch), pokud
                // volající nepředal abortOriginFetch. Předávali ho 3 volající z 50. U zbytku
                // neodešel žádný request, filtr v UI ukazoval nový stav a data zůstala stará —
                // a řádek nad tabulkou k těm starým datům seděl, takže na obrazovce nebylo nic,
                // co by nesedělo. Uživatel se díval na návrhy v přesvědčení, že vidí platné smlouvy.
                //
                // Zahodit uživatelovu změnu filtru není správně nikdy, takže tady už podmínka není.
                // `abortOriginFetch` zůstává jen pro větev s neviditelným seznamem výše.
                if (state[key].fetchRunning) {
                    this.store.dispatch(PerfectScrollbarActions.fetchOriginData({key}))
                }

                if (state[key].cachedData?.list?.length) {
                    this.store.dispatch(PerfectScrollbarActions.setOriginItemCollection({ key, collection: state[key].cachedData?.list }))
                    this.store.dispatch(PerfectScrollbarActions.updateOriginFilterCriteria({ key, fragment: { offset: state[key].cachedData?.offset } }))
                    this.store.dispatch(PerfectScrollbarActions.setTotalCount({ key, count: state[key].cachedData.totalCount }))
                    this.store.dispatch(PerfectScrollbarActions.setCachedData({ key, items: [], offset: undefined }))
                    setTimeout(() => {
                        const container: HTMLElement = document.getElementById(state[key].psSelector)
                        if (container) {
                            container.scrollTop = state[key].cachedData.scrollTop
                        }
                    }, 8)
                    return of(this.noFetch)
                }

                this.store.dispatch(PerfectScrollbarActions.setFlushCache({ key: key, flushCache: false }))

                return this.getGlobalStatePathValue(key, 'filterCriteria', state[key].targetKey).pipe(
                    take(1),
                    switchMap(filterCriteria => {
                        return this.getGlobalStatePathValue(key, 'itemCollection', state[key].targetKey).pipe(
                            take(1),
                            switchMap(itemCollection => {
                                // add cached data to collection immediately
                                if (state[key].resultCache?.length && filterCriteria?.offset) {
                                    const itemCollectionLength = (itemCollection.concat(state[key].resultCache)).length
                                    this.store.dispatch(PerfectScrollbarActions.setOriginItemCollection({
                                        key: key,
                                        collection: itemCollection.concat(state[key].resultCache)
                                    }))
                                    this.store.dispatch(PerfectScrollbarActions.originFilterCriteriaAction({
                                        key: key,
                                        fragment: { offset: filterCriteria.offset + state[key].resultCache?.length }
                                    }))
                                    // DEV-482: Clear prefetch cache after consumption to prevent re-append
                                    // on subsequent scroll triggers (would cause visible duplicates).
                                    this.store.dispatch(PerfectScrollbarActions.setResultCache({ key: key, items: [] }))

                                    if (itemCollectionLength >= state[key].totalCount) {
                                        return of(this.noFetch)
                                    }
                                }
                                // initial call also preloads cache
                                this.store.dispatch(PerfectScrollbarActions.updateOriginFilterCriteria({
                                    key: state[key].globalStatePath, fragment: { limit: filterCriteria?.offset ? this.itemsPerPage : 2 * this.itemsPerPage }})
                                )

                                // if request is filtered by search text reset on empty query
                                // DEV-482: Guard `!filterCriteria?.offset` ensures this branch only runs for an
                                // explicit search clear (setSearchText$ resets offset to 0). Without it, every
                                // scroll on a hybrid filter without prior search would hit `noFetch` because
                                // INIT_STATE.searchText is null and matched the `!searchText` test.
                                if (filterCriteria?.searchText !== undefined && !initialLoad && !filterCriteria?.searchText && !filterCriteria?.offset) {
                                    // DEV-376: Hybrid filters (refetchOnClearSearch=true) should NOT reset items
                                    // when searchText is empty — items must stay visible while refetchAfterClearSearch$
                                    // handles the reload. Search-only filters DO reset (clear dropdown).
                                    if (!state[key]?.refetchOnClearSearch) {
                                        this.store.dispatch(PerfectScrollbarActions.setOriginItemCollection({ key: key, collection: null }))
                                        this.store.dispatch(PerfectScrollbarActions.setTotalCount({ key: key, count: null }))
                                        this.store.dispatch(PerfectScrollbarActions.originFilterCriteriaAction({ key: key, fragment: { offset: 0 } }))
                                        this.store.dispatch(PerfectScrollbarActions.setLoadState({ key: key, loadState: false }))
                                    }
                                    return of(this.noFetch)
                                }
                                if (!itemCollection?.length) {
                                    this.store.dispatch(PerfectScrollbarActions.setLoadState({ key: key, loadState: true }))
                                }
                                return of(this.fetch)
                            }))
                    })
                )
            })
        ))
    }

    private postLoadAction = (key: string, data: any): void => {
        let flushCache: boolean
        if (data.aborted) {return}

        this.store.select(selectPerfectScrollbar).pipe(take(1)).subscribe(state => {
            // CA-16783 prevent code executing if list with PS not presents anymore (for exp. go to a contracts-list and immediately back)
            if (!state[key]?.globalStatePath) {
                return
            }
            flushCache = state[key].flushCache

            this.getGlobalStatePathValue(key, 'filterCriteria', state[key].targetKey).pipe(
                take(1)).
                // filter(filterCriteria => !!filterCriteria),
                subscribe(filterCriteria => {
                let items = data.items || data

                if (!filterCriteria.offset) {
                    const container: HTMLElement = document.getElementById(state[key].psSelector)
                    if (container) {
                        container.scrollTop = 0
                    }
                    this.store.dispatch(PerfectScrollbarActions.setResultCache({key: key, items: items.slice(this.itemsPerPage)}))
                    items = items.slice(0, this.itemsPerPage)
                    this.store.dispatch(PerfectScrollbarActions.originFilterCriteriaAction({key: key, fragment: {offset: filterCriteria.offset + items.length}}))
                    this.store.dispatch(PerfectScrollbarActions.setOriginItemCollection({key: key, collection: items}))
                    this.store.dispatch(PerfectScrollbarActions.setFlushCache({key: key, flushCache: false}))

                    // Wait for Angular change detection + browser paint before measuring DOM
                    setTimeout(() => {
                        requestAnimationFrame(() => {
                            Ps.update(container)
                            this.handleLazyLoad(key, container.scrollTop, container.scrollHeight - container.offsetHeight)
                        })
                    })
                } else {
                    this.store.dispatch(PerfectScrollbarActions.setResultCache({key: key, items: items.slice(0)}))
                    if (flushCache) {
                        setTimeout(() => {
                            this.prepareFetch(key).pipe(take(1)).subscribe(() => {
                            })
                        })
                    }
                }

            })
        })
    }

    private getGlobalStatePathValue(key: string, attribute: string, targetKey: string): Observable<any | any[]> {
        // Remove targetKey from path :target-key in case of components using dynamic reducers
        const updatedKey = key ? key.replace(/:.*/, '') : key
        return this.store.select(selectOriginFeatureByKey(updatedKey, attribute, targetKey))
    }

    // init PS plugin in DOM
    private     initPerfectScrollbar(key: string, selector: string): Promise<void> {
        return new Promise((resolve) => {
            const container: HTMLElement = document.getElementById(selector)
            Ps.initialize(container, {   wheelSpeed: 1,   wheelPropagation: false,   minScrollbarLength: 32 })
            container?.addEventListener('ps-scroll-y', () => {
                this.handleLazyLoad(
                    key,
                    container.scrollTop,
                    container.scrollHeight - container.offsetHeight)
                this.store.dispatch(PerfectScrollbarActions.setCachedScroll({key, scrollTop: container.scrollTop}))
            })
            // @ts-ignore
            setTimeout(() => {Ps.update(container)})
            resolve()
        })
    }

    private checkInitialLoad(key: string, targetKey: string, initialLoad: boolean): void {
        this.getGlobalStatePathValue(key, 'filterCriteria', targetKey).pipe(take(1)).subscribe(filterCriteria => {
            if(filterCriteria?.searchText?.length) {
                setTimeout(() => {
                    this.store.dispatch(PerfectScrollbarActions.filterChanged({key: key}))
                })
            }
            if (initialLoad) {
                this.store.dispatch(PerfectScrollbarActions.filterChanged({key: key, initialLoad: true}))
            }
        })
    }

    private handleLazyLoad(key: string, scrollTop: number, availableHeight: number): void {
        this.store.select(selectPerfectScrollbarFeature).pipe(take(1)).subscribe(state => {
            this.getGlobalStatePathValue(key, 'itemCollection', state[key].targetKey).pipe(
            take(1)).subscribe(itemCollection => {
                if (scrollTop + 200 >= availableHeight && (itemCollection === null || (itemCollection && state[key].totalCount > itemCollection.length))) {
                    if (!itemCollection?.length) {
                        this.store.dispatch(PerfectScrollbarActions.setLoadState({key, loadState: true}))
                    }
                    this.prepareFetch(key, state[key].initialLoad, false).pipe(take(1)).subscribe( res => {
                        if (res.fetch) {
                            this.store.dispatch(PerfectScrollbarActions.fetchOriginData({ key }))
                        }
                    })
                } else {
                    this.store.dispatch(PerfectScrollbarActions.setLoadState({key, loadState: false}))
                }
            })
        })
    }

}